import { Stack } from '@mui/material';
import { t } from 'i18next';
import { StackTraceTable } from '../../../../../common/calls/components/StackTraceTable';
import { CallsTableController } from '../../../../../common/calls/controllers/CallsTableController';
import { Call } from '../../../../../common/calls/models/Call';
import { BaseController, BaseState } from '../../../../../common/controllers/BaseController';
import { Severity } from '../../../../../common/enums/Severity';
import { withRouter, WithRouterProps } from '../../../../../common/higher-order-components/with-router';
import { LocationsTable } from '../../../../../common/locations/components/LocationsTable';
import { Location } from '../../../../../common/locations/models/Location';
import { LocationVariant } from '../../../../../common/locations/services/LocationsService';
import { AuthUserContext } from '../../../../../common/services/AuthUserContext';
import { PaginationResult } from '../../../../../common/types/pagination';
import { ModelType } from '../../../../../common/utils/GQLIdEncoder';
import { bind } from '../../../../../common/utils/decorators';
import { JiraIssue } from '../../../../../integrations/jira/models/valueObjects/JiraIssue';
import { JiraRepository } from '../../../../../integrations/jira/services/JiraRepository';
import { JiraUtils } from '../../../../../integrations/jira/utils/JiraUtils';
import { IntegrationsRepository } from '../../../../../integrations/services/IntegrationsRepository';
import { ReportIssuesContextType } from '../../../common/contexts/ReportIssuesContext';
import { Details } from '../components/Details';
import { Overview } from '../components/Overview';
import { Instance } from '../models/Instance';
import { Operation } from '../models/Operation';
import { DEFAULT_INSTANCE_CALLS_PAGE_SIZE, DEFAULT_INSTANCE_LOCATIONS_PAGE_SIZE, InstanceRepository } from '../services/InstanceRepository';
import { InstanceOperationsController } from './InstanceOperationsController';
import { JiraExport } from '../../../../../integrations/jira/models/JiraExport';
import { ProjectJira } from '../../../../../integrations/jira/models/ProjectJira';
import { ErrorNotification, SuccessNotification } from '../../../../../common/utils/notifications/Notification';
import { ServiceNowRepository } from '../../../../../integrations/service-now/services/ServiceNowRepository';
import { FeatureFlagsType, getFeatureFlag } from '../../../../../config/launch-darkly/FeatureFlagsController';

type Props = WithRouterProps<ReportIssuesContextType> & {};

type State = BaseState & {
  loadingInstance: boolean;
  showJiraExport: boolean;
  jiraExport: JiraExport | null;
  jiraExportUrl: string | null;
  projectJira: ProjectJira | null;
  showServiceNow: boolean;
  serviceNowExportUrl: string | null;
};

class Controller extends BaseController<Props, State> {
  private readonly instanceRepository = new InstanceRepository();
  private readonly integrationsRepository = new IntegrationsRepository();
  private readonly jiraRepository = new JiraRepository();
  private readonly serviceNowRepository = new ServiceNowRepository();

  private reportId: number | null;
  private instance: Instance | null;
  private initialCalls: PaginationResult<Call> | null;
  private initialOperations: PaginationResult<Operation> | null;
  private initialLocations: PaginationResult<Location> | null;
  private jiraIssueCollector: string | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.reportId = null;
    this.instance = null;
    this.initialCalls = null;
    this.initialOperations = null;
    this.initialLocations = null;
    this.jiraIssueCollector = null;

    this.state = {
      ...super.getBaseState(),
      loadingInstance: true,
      showJiraExport: false,
      jiraExport: null,
      jiraExportUrl: null,
      projectJira: null,
      showServiceNow: false,
      serviceNowExportUrl: null,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    if (this.props.context.reportReady) {
      // When the component mounts, only fetch the Instance if the parent report is ready.
      // `ReportController` will take care of redirecting to the parent URL if it is not,
      // and we should avoid trying to fetch an Instance that may not exist
      this.fetchInstance();
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    if (!prevProps.context.reportReady && this.props.context.reportReady) {
      // Handle the case where the Report was not ready at component mount
      this.fetchInstance();
    }

    if (Number.parseId(this.props.params['instanceId']) !== Number.parseId(prevProps.params['instanceId'])) {
      // The Instance has changed, the controller is not re-mounted.
      // Handle the refresh of the new selected Instance
      this.fetchInstance();
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.instanceRepository.cancelAllRequests();
    this.integrationsRepository.cancelAllRequests();
  }

  async fetchInstance() {
    try {
      this.setState({ loadingInstance: true });
      const { reportId, issueNumber, instanceNumber } = this.getPathParams();

      const { instance, initialCalls, initialOperations, initialLocations } = await this.instanceRepository.getInstance(reportId, issueNumber, instanceNumber);
      this.reportId = reportId;
      this.instance = instance;
      this.initialCalls = initialCalls;
      this.initialOperations = initialOperations;
      this.initialLocations = initialLocations;

      // Only evaluate integrations if the instance can be exported
      let showJiraExport = false;
      let showServiceNow = false;
      if (instance.getSeverity() !== Severity.PASSED) {
        const [integrations, jiraExport, projectJira] = await Promise.all([
          this.integrationsRepository.getIntegrations(),
          this.jiraRepository.jiraIntegration(),
          this.jiraRepository.getProjectJira(instance.getProjectId()),
        ]);
        this.jiraIssueCollector = projectJira?.getIssueCollectorCode() ?? integrations.getJiraIssueCollector()?.getIssueCollectorCode() ?? null;
        showJiraExport = Boolean(integrations.getJiraExport() || this.jiraIssueCollector);
        this.setState({ jiraExport, projectJira });
        // we want to show ServiceNow export if it is enabled by feature flag and properly configured
        showServiceNow = !!getFeatureFlag(FeatureFlagsType.ENABLE_SERVICE_NOW) && !!integrations.getServiceNow();
      }
      this.setState({
        showJiraExport,
        loadingInstance: false,
        showServiceNow,
        jiraExportUrl: instance.getJiraExportUrl(),
        serviceNowExportUrl: instance.getServiceNowExportUrl(),
      });
      this.props.context.onInstanceLoaded({
        id: instance.getId(),
        numberLocal: instance.getLocalNumber(),
        keptByFilters: instance.isKeptByFilters(),
      });
    } catch (e: unknown) {
      this.setState({ loadingInstance: false });
      this.handleError(e, { invalidUrl: t('nonExistingInstance') });
    }
  }

  @bind
  async handleJiraExport(): Promise<JiraIssue | null> {
    if (!this.instance) return null;

    const projectKey = this.state.projectJira?.getProjectKey() ?? this.state.jiraExport?.getJiraProjectKey();
    const issueType = this.state.projectJira?.getIssueTypeKey() ?? this.state.jiraExport?.getJiraIssuetypeKey();

    try {
      if (projectKey && issueType) {
        const testConnectionResult = await this.jiraRepository.checkJiraExport(projectKey, issueType);
        if (!testConnectionResult.isSuccessful()) {
          const message = testConnectionResult.getReason() ?? t('integrationList.jiraExport.testFail');
          this.showMessage(new ErrorNotification(t('jiraExportFailure', { error: message })));
          return null;
        }
      }

      const response = await this.jiraRepository.exportInstance(this.instance.getId());
      const issues = response.getIssues();
      if (issues.length === 1) {
        this.setState({ jiraExportUrl: issues[0].getUrl() });
      }
      this.showMessages([response.getMessage()]);
      return null;
    } catch (e: unknown) {
      this.handleError(e);
      return null;
    }
  }

  @bind
  async handleJiraIssueCollector(): Promise<null> {
    if (!this.instance || !this.jiraIssueCollector) return null;

    try {
      const severity = this.instance.getSeverity();
      const fullname = AuthUserContext.getUser().getName();
      const summary = `${this.instance.getRuleNumber()}.${this.instance.getLocalNumber()} ${this.instance.getRuleTitle()}`;
      const description = `${this.instance.getData()}\n\n${this.instance.getShortExplanation()}`;

      await JiraUtils.loadIssueCollectorScript(this.jiraIssueCollector, {
        severity,
        fullname,
        summary,
        description,
      });

      await window.showIssueCollector?.();
      return null;
    } catch (e: unknown) {
      this.handleError(e, { fallback: e instanceof Error ? e.message : undefined });
      return null;
    }
  }

  @bind
  async handleServiceNowExport() {
    if (!this.instance) {
      return;
    }
    try {
      const serviceNowExportedRecord = await this.serviceNowRepository.exportInstance(this.instance.getId());
      if (serviceNowExportedRecord) {
        this.showMessage(new SuccessNotification(t('serviceNowExport.singleExportSuccess')));
      }
      this.setState({ serviceNowExportUrl: serviceNowExportedRecord.getUrl() });
    } catch (e: unknown) {
      this.handleError(e);
    }
  }

  private getPathParams() {
    const { reportId, issueId: issueNumber, instanceId: instanceNumber } = this.parseNumericIds(['reportId', 'issueId', 'instanceId']);
    return { reportId, issueNumber, instanceNumber };
  }

  render() {
    const { initialIssuesLoaded, initialIssueLoaded, initialInstanceLoaded, includeFilteredItems } = this.props.context;
    const { loadingInstance, showJiraExport, showServiceNow, serviceNowExportUrl, jiraExportUrl } = this.state;
    const { reportId, issueNumber, instanceNumber } = this.getPathParams();

    // Although the `initialLoad` would correspond to the instance having being fetched,
    // we need to account for the synchronization mechanism of the whole layout.
    // Keep the initial load display as long as all columns of the layout have loaded
    // and the instance has been retrieved
    const initialLoad = !this.instance || !(initialIssuesLoaded && initialIssueLoaded && initialInstanceLoaded);

    const id = this.instance?.getId() ?? instanceNumber;
    const projectId = this.instance?.getProjectId() ?? 0;
    const details = this.instance?.getDetails() ?? null;
    const locations = this.instance?.getHardCodedStringLocations() ?? null;
    const traceType = this.instance?.getTraceType() ?? undefined;
    const jiraExportCallback = this.jiraIssueCollector ? this.handleJiraIssueCollector : this.handleJiraExport;

    return this.renderBase(
      <Stack spacing={2}>
        <Overview
          instance={this.instance}
          includeFilteredItems={includeFilteredItems}
          loading={loadingInstance}
          initialLoad={initialLoad}
          onJiraExport={showJiraExport ? jiraExportCallback : undefined}
          onServiceNowExport={showServiceNow ? this.handleServiceNowExport : undefined}
          serviceNowExportUrl={serviceNowExportUrl}
          onError={this.handleError}
          jiraExportUrl={jiraExportUrl}
        />
        <Details details={details} loading={loadingInstance} initialLoad={initialLoad} />
        {(initialLoad || (locations && locations.length > 0)) && (
          <StackTraceTable
            // Re-mount the table when a new Instance is loaded to reset the table state
            key={`hard_coded_locations_${id}`}
            title={t('hardCodedStringSourceCodeLocations')}
            initialFrames={locations}
            parentLoading={loadingInstance || initialLoad}
          />
        )}
        {(!this.initialLocations || this.initialLocations.data.length > 0) && (
          <LocationsTable
            // Re-mount the table when a new Instance is loaded to load the new prefilled initial locations
            key={`locations_${id}`}
            className="instance-locations"
            variantId={id}
            variant={LocationVariant.Instance}
            initialData={this.initialLocations}
            parentLoading={initialLoad || loadingInstance}
            defaultPageSize={DEFAULT_INSTANCE_LOCATIONS_PAGE_SIZE}
            withPrefill
          />
        )}
        {(!this.initialOperations || this.initialOperations.data.length > 0) && (
          // Re-mount the table when a new Instance is loaded to load the new prefilled initial operations
          <InstanceOperationsController
            key={`operations_${id}`}
            reportId={reportId}
            issueNumber={issueNumber}
            instanceNumber={instanceNumber}
            instanceId={id}
            initialOperations={this.initialOperations}
            parentLoading={initialLoad || loadingInstance}
          />
        )}
        {(!this.initialCalls || this.initialCalls.data.length > 0) && (
          // Re-mount the table when a new Instance is loaded to load the new prefilled initial calls
          <CallsTableController
            key={`calls_${id}`}
            variant={ModelType.Instance}
            variantId={id}
            defaultPageSize={DEFAULT_INSTANCE_CALLS_PAGE_SIZE}
            projectId={projectId}
            traceType={traceType}
            initialCalls={this.initialCalls}
            parentLoading={initialLoad || loadingInstance}
          />
        )}
      </Stack>
    );
  }
}

export const InstanceController = withRouter<ReportIssuesContextType>(Controller);
